'use client';
import React, { ReactNode, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setSidenavVisible } from '@/lib/slices/nav-slice';
import { setAdminStudentView } from '@/lib/slices/course-slice';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { resetActivitySlice } from '@/lib/slices/activity-slice';

import Button from './ui/Button';
import ActivityIcon from './ActivityIcon';
import ToggleButton from '@/common/components/ToggleButton';
import ProgressCircle from './ProgressCircle';
import { CheckCircleIcon } from '@/assets/ui/activity';
import CourseCodeDisplay from '@/common/components/course/management/CourseCodeDisplay';
import { QuizitIcon } from '@/assets/ui/study';

import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import CourseUtilityService from '@/services/course/course-utility-service';
import UtilityService from '@/services/util/utility-service';
import AuroraUtilityService from '@/services/util/aurora-utililty-service';
import { ActivityData, CourseData, UnitData } from 'course-types';
import { role } from 'auth-types';

interface BreadCrumb {
  displayName: string;
  pushPath: string;
}

function cleanString(s: string): string {
  return s
    .split('-')
    .map((ss) => ss.charAt(0).toUpperCase() + ss.slice(1))
    .join(' ');
}

function TopNav(): React.JSX.Element | null {
  const router = useRouter();
  const pathName = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);
  const darkMode = useAppSelector(() => store.getState().navigation.darkMode);
  const visible = useAppSelector(() => store.getState().navigation.visible);
  const activity: ActivityData = useAppSelector(() => store.getState().activity);
  const course: CourseData = useAppSelector(() => store.getState().course.activeCourseData);
  const adminStudentView: boolean = useAppSelector(() => store.getState().course.adminStudentView);
  const unitId = useAppSelector(() => store.getState().unit.id);
  const subunitId = useAppSelector(() => store.getState().subunit.id);
  const [isCompleted, setIsCompleted] = useState(false);
  const [units, setUnits] = useState<Array<[string, UnitData]>>([]);

  // Check if we're on the course page
  const isOnCoursePage =
    (pathName.includes('/student/course') &&
      !pathName.includes('/lesson') &&
      !pathName.includes('/student/courses')) ||
    (pathName.includes('/admin/courses') && course.id);

  // Fetch course units data when course changes
  useEffect(() => {
    if (!course.id) return;

    const fetchUnits = async () => {
      try {
        const courseData = await UserCourseStorageService.getCourseData();
        if (courseData && courseData.units) {
          const unitsArray = Object.entries(courseData.units);
          setUnits(unitsArray);
        }
      } catch (error) {
        console.error('Error fetching course units:', error);
      }
    };

    fetchUnits();
  }, [course.id]);

  useEffect(() => {
    if (!activity.id) return;

    try {
      const { activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
        course.id,
        unitId,
        subunitId,
      );

      if (activity.type === 'video') {
        setIsCompleted(!!activityData.video[activity.id]?.submissionDate);
      } else if (activity.type === 'document') {
        setIsCompleted(!!activityData.document[activity.id]?.submissionDate);
      }
    } catch {
      setIsCompleted(false);
    }
  }, [activity]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidenav = () => {
    dispatch(setSidenavVisible(!visible));
  };

  const isOnAdminCourses = pathName.startsWith('/admin/courses');
  const isAdminCoursesRoot = pathName === '/admin/courses' || pathName === '/admin/courses/';
  const isStudentCourseRoot =
    pathName.startsWith('/student/course') && !pathName.includes('/student/courses');
  const isStudentLessonRoot = pathName.startsWith('/student/lesson');
  const showGradient = isStudentCourseRoot || isStudentLessonRoot || isAdminCoursesRoot;

  const resetAdminStudentViewState = () => {
    dispatch(resetActivitySlice());
    dispatch(setSubunitId(undefined));
    dispatch(setUnitId(''));
  };

  // Ensure admin student view resets when leaving admin courses root
  useEffect(() => {
    if (!isAdminCoursesRoot && adminStudentView) {
      dispatch(setAdminStudentView(false));
      resetAdminStudentViewState();
    }
  }, [pathName]);

  // split up the pathname into its slices
  const slices = pathName.substring(1, pathName.length - 1).split('/');

  const breadCrumbs = slices.flatMap((slice, i) => {
    const crumbs: BreadCrumb[] = [];

    if (slice === 'lesson') {
      // Add Course Overview crumb before lesson
      crumbs.push({ displayName: 'Course Overview', pushPath: 'student/course' });
      crumbs.push({ displayName: course.name, pushPath: slices.slice(0, i + 1).join('/') });
      return crumbs;
    }

    if (slice === 'edit') {
      crumbs.push({
        displayName: activity.name,
        pushPath: slices.slice(0, i + 1).join('/') + `/?id=${activity.id}`,
      });
      return crumbs;
    }

    const crumb: BreadCrumb = {
      displayName: cleanString(slice),
      pushPath: slices.slice(0, i + 1).join('/'),
    };

    return [crumb];
  });

  const getBreadCrumb = (): ReactNode => {
    if (userRole === '') return <></>;

    // parent ul
    return breadCrumbs.map((crumb, i) => (
      <div key={i} className='flex flex-row'>
        <li
          onClick={() => {
            router.push(`/${crumb.pushPath}`);
          }}
          className={`cursor-pointer ${i === breadCrumbs.length - 1 ? (darkMode ? 'text-primary_highlight' : 'text-primary') : 'text-text_gray_600'} hover:underline font-normal`}
        >
          {crumb.displayName}
        </li>

        <p className={`${i === breadCrumbs.length - 1 ? 'hidden' : 'mr-2'} text-text_gray_600`}>
          {' '}
          /{' '}
        </p>
      </div>
    ));
  };

  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleMarkComplete = async () => {
    try {
      if (activity.type === 'video' || activity.type === 'document') {
        await ActivityUtilityService.onActivityComplete(
          course.id,
          unitId,
          subunitId,
          activity.id,
          activity.type,
        );
      }
      // Update the completion status after successful completion
      setIsCompleted(true);
    } catch (error) {
      toast.error('Failed to mark activity as complete. Try again.', { duration: 3000 });
    }
  };

  const handleTakeSelfQuiz = () => {
    void AuroraUtilityService.generateDocumentQuiz(
      activity.id,
      subunitId,
      unitId,
      dispatch,
      activity.name,
    );
    router.push('/student/study/self-quiz');
  };

  const getCourseInfo = (): ReactNode => {
    return (
      <div className='flex flex-row gap-3 px-8 items-center justify-center'>
        <ActivityIcon type={activity.type} border />
        <div className='flex flex-col item items-start gap-1 grow'>
          <div className='flex flex-row item items-center gap-2'>
            <div className='py-0 w-fit text-2xl font-semibold text-text_gray_900'>
              {UtilityService.cleanInvalidWhitespace(activity.name ?? '')}
            </div>
            <div className='rounded-full bg-surface_gray_300 px-2 py-1 text-sm text-text_gray_700'>
              {capitalize(activity.type)}
            </div>
          </div>
          {/* TODO: Add activity details */}
          {/* <div className='flex flex-row item items-center gap-4'>
            <div className='text-gray-600 pl-[6px] pr-2 py-[2px] text-xs font-semibold gap-1 flex flex-row items-center'><ClockIcon className='stroke-current' />5 mins</div>
            <div className='text-gray-600 text-xs font-semibold gap-2'>Due in x days</div>
          </div> */}
        </div>
        {/* Right-side control: on student side show Mark Complete */}
        {!isOnAdminCourses && (activity.type === 'video' || activity.type === 'document') && (
          <div className='flex flex-col items-end gap-2'>
            <Button
              onClick={handleMarkComplete}
              className='bg-blue-600 text-white rounded-full py-1 pl-3 pr-[10px] h-fit flex items-center justify-center gap-1'
            >
              <CheckCircleIcon className='stroke-current size-[12px]' />
              <div className='text-white text-xs font-normal text-nowrap'>
                {isCompleted ? 'Completed' : 'Mark as complete'}
              </div>
            </Button>
            {activity.type === 'document' && isCompleted && (
              <Button
                onClick={handleTakeSelfQuiz}
                className='bg-none text-primary border border-primary rounded-full py-1 pl-3 pr-[10px] h-fit flex items-center justify-center gap-1'
                outline
              >
                <QuizitIcon className='text-primary size-[12px]' />
                <div className='text-primary text-xs font-normal text-nowrap'>Take a Quiz</div>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  const getCoursePageInfo = (): ReactNode => {
    // Check if we're on admin courses page (not in student view)
    const isAdminCoursePage = pathName.includes('/admin/courses') && !adminStudentView;

    // For admin view, show course name only (no progress)
    if (isAdminCoursePage) {
      return (
        <div className='flex flex-row mb-[-16px] md:px-8 lg:px-12 px-4 items-start justify-between'>
          <h1 className='font-semibold text-2xl color-text_gray_900 mb-4'>{course.name}</h1>
          {isAdminCoursesRoot && course.id && !adminStudentView && <CourseCodeDisplay />}
        </div>
      );
    }

    // Student view with progress (original logic)
    const progressPercentage = ProgressUtilityService.calculateCourseProgress(course.id);
    const totalActivities = CourseUtilityService.calculateTotalActivities(units);
    const progress = UserProgressStorageService.getUserCourseProgressCookie();
    let completedActivities = 0;

    if (progress && progress[course.id]) {
      const data = progress[course.id].completion_data;

      // Count completed activities in units
      Object.values(data.units).forEach((unit) => {
        // Count completed quizzes
        Object.values(unit.quiz ?? {}).forEach((quiz) => {
          if ((quiz as { attempts?: number }).attempts! > 0) completedActivities++;
        });

        // Count completed documents and videos
        (['document', 'video'] as const).forEach((type) => {
          const collection = unit[type] ?? {};
          Object.values(collection).forEach((item) => {
            if ((item as { submissionDate?: Date }).submissionDate) completedActivities++;
          });
        });
      });

      // Count completed activities in subunits
      Object.values(data.subunits).forEach((subunit) => {
        // Count completed quizzes
        Object.values(subunit.quiz ?? {}).forEach((quiz) => {
          if ((quiz as { attempts?: number }).attempts! > 0) completedActivities++;
        });

        // Count completed documents and videos
        (['document', 'video'] as const).forEach((type) => {
          const collection = subunit[type] ?? {};
          Object.values(collection).forEach((item) => {
            if ((item as { submissionDate?: Date }).submissionDate) completedActivities++;
          });
        });
      });
    }

    return (
      <div className='flex flex-col mb-[-16px] md:px-8 lg:px-12 px-4 items-start justify-center'>
        <h1 className='font-semibold text-2xl color-text_gray_900 mb-4'>{course.name}</h1>
        <div className='flex flex-row gap-2 items-center'>
          <ProgressCircle size={60} progress={progressPercentage} />
          <div className='flex flex-col'>
            <span className='text-text_gray_900 text-lg font-semibold'>Course Progress</span>
            <span className='text-text_gray_900 text-md font-normal'>
              {completedActivities} of {totalActivities} activities completed
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Create dynamic gradient based on branding colors and dark mode
  const getGradientStyle = (): React.CSSProperties => {
    if (darkMode) {
      // Dark mode: darker variants with reduced opacity
      return {
        background: `linear-gradient(to right, 
          color-mix(in srgb, var(--color-brand-highlight) 15%, #000000), 
          color-mix(in srgb, var(--color-brand-mid) 12%, #000000))`,
      };
    } else {
      // Light mode: brand colors with reduced opacity over #fafafa
      return {
        background: `linear-gradient(to right, 
        color-mix(in srgb, var(--color-brand-highlight) 25%, #fafafa), 
        color-mix(in srgb, var(--color-brand-mid) 20%, #fafafa))`,
      };
    }
  };

  return (
    <nav
      className='navbar lg:px-[120px] py-[32px] h-fit gap-4 flex flex-col border-t border-border_gray_300'
      style={showGradient ? getGradientStyle() : undefined}
    >
      <div className='flex flex-row justify-between md:px-8 lg:px-12 px-4 items-center'>
        <div className='flex flex-row gap-4 items-center'>
          {isMobile && !visible && (
            <div className='flex flex-row gap-4 h-0 mr-4 flex-shrink-0 xl:hidden'>
              <button
                onClick={toggleSidenav}
                className='p-2 h-fit rounded-md bg-background_white shadow-md border border-border_sidenav -translate-y-[50%]'
                aria-label='Toggle navigation menu'
              >
                <div className='w-6 h-6 flex flex-col justify-center items-center'>
                  <span className='block w-5 h-0.5 bg-text_gray_600 mb-1'></span>
                  <span className='block w-5 h-0.5 bg-text_gray_600 mb-1'></span>
                  <span className='block w-5 h-0.5 bg-text_gray_600'></span>
                </div>
              </button>
            </div>
          )}
          <ul className='horizontal-list px-0 m-0 flex'>{getBreadCrumb()}</ul>
        </div>
        {isAdminCoursesRoot && course.id && (
          <div className='flex items-center gap-2'>
            <span className={`${adminStudentView ? 'font-semibold' : ''} text-sm`}>
              Student View
            </span>
            <ToggleButton
              onClick={() => {
                const next = !adminStudentView;
                dispatch(setAdminStudentView(next));
                if (!next) {
                  resetAdminStudentViewState();
                }
              }}
              displayIcons={false}
              initialState={adminStudentView}
            />
          </div>
        )}
      </div>
      {isOnCoursePage
        ? activity.id
          ? getCourseInfo()
          : getCoursePageInfo()
        : isStudentLessonRoot && activity.id
          ? getCourseInfo()
          : null}
    </nav>
  );
}

export default TopNav;
